import React from "react";
import _ from "lodash";
import { UnControlled as CodeMirror } from "react-codemirror2";
import "codemirror/lib/codemirror.css";
import "codemirror/mode/javascript/javascript";
import "codemirror/addon/fold/foldcode";
import "codemirror/addon/fold/foldgutter";
import "codemirror/addon/fold/brace-fold";
import "codemirror/addon/fold/foldgutter.css";
import "codemirror/addon/selection/active-line";
import "codemirror/addon/edit/matchbrackets";
import { stringifyJson, checkIsValidJson } from "helpers/JsonCheck";

const BG_HINT_COLOR = "bg-orange-100";

const buttonClassBlue =
  "px-2 py-1 bg-blue-200 text-xs rounded border border-gray-400 hover:bg-blue-300 text-gray-900 font-semibold";

function getLCS(A, B) {
  const dp = Array(A.length + 1)
    .fill(null)
    .map(() => Array(B.length + 1).fill(0));

  for (let i = 1; i <= A.length; i++) {
    for (let j = 1; j <= B.length; j++) {
      dp[i][j] =
        A[i - 1] === B[j - 1]
          ? dp[i - 1][j - 1] + 1
          : Math.max(dp[i - 1][j], dp[i][j - 1]);
    }
  }

  let i = A.length;
  let j = B.length;
  const lcs = [];

  while (i > 0 && j > 0) {
    if (A[i - 1] === B[j - 1]) {
      lcs.unshift(A[i - 1]);
      i--;
      j--;
    } else if (dp[i - 1][j] > dp[i][j - 1]) {
      i--;
    } else {
      j--;
    }
  }

  return lcs;
}

function findDiffIndices(A, B) {
  const lcs = getLCS(A, B);

  let lcsIndex = 0;

  return _.reduce(
    A,
    (result, value, index) => {
      if (value === lcs[lcsIndex]) {
        lcsIndex++;
      } else {
        result.push(index);
      }
      return result;
    },
    []
  );
}

class EditableJsonEditor extends React.Component {
  constructor(props) {
    super(props);

    const initialText = JSON.stringify(props.originalConfig || {}, null, 4);

    this.state = {
      originalLines: initialText.split("\n"),
      editorValue: initialText,
      changedLines: [],
    };

    this.editorRef = React.createRef();

    this.debouncedOnChange = _.debounce(this.handleCommitChange, 200);
    this.handleEditorDidMount = this.handleEditorDidMount.bind(this);
    this.handleCommitChange = this.handleCommitChange.bind(this);
    this.handleFormatJson = this.handleFormatJson.bind(this);
    this.handleCheckFormat = this.handleCheckFormat.bind(this);
  }

  componentDidMount() {
    if (this.editorRef && this.editorRef.current) {
      this.editorRef.current.editor.setValue(this.state.editorValue);
    }
  }

  componentWillUnmount() {
    if (this.editorRef.current && this.editorRef.current.editor) {
      const wrapper = this.editorRef.current.editor.getWrapperElement();
      wrapper.removeEventListener("mousedown", this._handleStopPropagation);
      wrapper.removeEventListener("click", this._handleStopPropagation);
      wrapper.removeEventListener("dblclick", this._handleStopPropagation);
    }
  }

  handleEditorDidMount(editor) {
    const wrapper = editor.getWrapperElement();

    wrapper.addEventListener("mousedown", this._handleStopPropagation);
    wrapper.addEventListener("click", this._handleStopPropagation);
    wrapper.addEventListener("dblclick", this._handleStopPropagation);
  }

  handleCommitChange(value) {
    const { originalLines } = this.state;
    const currentLines = value.split("\n");
    const changedLines = findDiffIndices(currentLines, originalLines);

    let parsed = null;
    try {
      parsed = JSON.parse(value);
    } catch (e) {}

    if (this.props.onChange) {
      this.props.onChange({
        raw: value,
        parsed,
      });
    }

    const editor = this.editorRef.current.editor;
    editor.eachLine((lineHandle) => {
      editor.removeLineClass(lineHandle, "background", BG_HINT_COLOR);
    });
    changedLines.forEach((line) => {
      editor.addLineClass(line, "background", BG_HINT_COLOR);
    });
  }

  handleFormatJson() {
    const result = this.handleCheckFormat();
    if (_.isNil(result)) return;

    const formatted = stringifyJson(result);

    if (this.editorRef && this.editorRef.current) {
      this.editorRef.current.editor.setValue(formatted);
    }
  }

  handleCheckFormat() {
    if (this.editorRef && this.editorRef.current) {
      const editorValue = this.editorRef.current.editor.getValue();
      const result = checkIsValidJson(editorValue);

      if (this.props.handleCheckFormat) {
        this.props.handleCheckFormat({ result: !_.isNil(result) });
      }

      return result;
    }
  }

  _handleStopPropagation(e) {
    e.stopPropagation();
  }

  render() {
    const { readOnly = false, customClass = "" } = this.props;
    return (
      <>
        <div
          className={`editor-wrapper border mb-4 overflow-y-auto border-gray-400 ${
            readOnly ? "read-only" : ""
          }  ${customClass}`}
          style={{ height: readOnly ? "" : "calc(100% - 2.5rem)" }}
        >
          <CodeMirror
            ref={this.editorRef}
            editorDidMount={this.handleEditorDidMount}
            options={{
              mode: "application/json",
              theme: "default",
              lineNumbers: true,
              foldGutter: true,
              gutters: ["CodeMirror-linenumbers", "CodeMirror-foldgutter"],
              styleActiveLine: true,
              matchBrackets: true,
              lineWrapping: true,
              readOnly,
              viewportMargin: 10,
            }}
            onChange={(editor, data, value) => {
              this.debouncedOnChange(value);
            }}
          />
        </div>
        {!readOnly && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              className={buttonClassBlue}
              onClick={this.handleFormatJson}
            >
              Format JSON
            </button>
            <button
              type="button"
              className={buttonClassBlue}
              onClick={this.handleCheckFormat}
            >
              Check Format
            </button>
          </div>
        )}
      </>
    );
  }
}

export default EditableJsonEditor;
